/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsSetSetupMethod } from "../funcs/identityProviderConnectionsSetSetupMethod.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetIdentityProviderConnectionSetupMethodRequest,
  SetIdentityProviderConnectionSetupMethodSecurity,
} from "../models/operations/setidentityproviderconnectionsetupmethod.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetIdentityProviderConnectionSetupMethodMutationVariables = {
  request: SetIdentityProviderConnectionSetupMethodRequest;
  security?: SetIdentityProviderConnectionSetupMethodSecurity | undefined;
  options?: RequestOptions;
};

export type SetIdentityProviderConnectionSetupMethodMutationData =
  OktaIdentityProviderConnection;

export type SetIdentityProviderConnectionSetupMethodMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setSetupMethod identityProviderConnections
 *
 * @remarks
 * Switch how a pending connection connects to Okta: an install from the Okta Integration Network (client secret) or a custom API Services app (private key). Allowed until the client ID is submitted. Switching to the custom app provisions a signing key and JWKS URL once; switching away stops serving the key and switching back reuses it. A key left unused is retired when the client ID is submitted or the connection is revoked. Requires org:admin.
 */
export function useSetIdentityProviderConnectionSetupMethodMutation(
  options?: MutationHookOptions<
    SetIdentityProviderConnectionSetupMethodMutationData,
    SetIdentityProviderConnectionSetupMethodMutationError,
    SetIdentityProviderConnectionSetupMethodMutationVariables
  >,
): UseMutationResult<
  SetIdentityProviderConnectionSetupMethodMutationData,
  SetIdentityProviderConnectionSetupMethodMutationError,
  SetIdentityProviderConnectionSetupMethodMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetIdentityProviderConnectionSetupMethodMutation(client, options),
    ...options,
  });
}

export function mutationKeySetIdentityProviderConnectionSetupMethod(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "setSetupMethod"];
}

export function buildSetIdentityProviderConnectionSetupMethodMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetIdentityProviderConnectionSetupMethodMutationVariables,
  ) => Promise<SetIdentityProviderConnectionSetupMethodMutationData>;
} {
  return {
    mutationKey: mutationKeySetIdentityProviderConnectionSetupMethod(),
    mutationFn: function setIdentityProviderConnectionSetupMethodMutationFn({
      request,
      security,
      options,
    }): Promise<SetIdentityProviderConnectionSetupMethodMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(identityProviderConnectionsSetSetupMethod(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
